import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Input } from '../ui/input';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { FieldHeader, FieldHelpText } from './FieldParts';

interface NameValue {
  prefix?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
}

interface Props {
  element: DocumentElementType;
}

const NameInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<NameValue>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    required?: boolean;
    helpText?: string;
    showPrefix?: boolean;
    showMiddleName?: boolean;
    showSuffix?: boolean;
    hidden?: boolean;
  };
  const label = data?.label || 'Name';
  const subLabel = data?.subLabel;
  const required = data?.required || false;
  const helpText = data?.helpText;
  const showPrefix = data?.showPrefix || false;
  const showMiddleName = data?.showMiddleName || false;
  const showSuffix = data?.showSuffix || false;

  const handleChange = (field: keyof NameValue, fieldValue: string) => {
    setValue({
      ...value,
      [field]: fieldValue,
    });
  };

  // Read-only (e.g. a completed result): render the composed name parts
  // as static text — no inputs. Only includes the subfields the form is
  // configured to show.
  if (isReadOnly) {
    const parts = [
      showPrefix ? value?.prefix : undefined,
      value?.firstName,
      showMiddleName ? value?.middleName : undefined,
      value?.lastName,
      showSuffix ? value?.suffix : undefined,
    ]
      .map((p) => (p || '').trim())
      .filter((p) => p.length > 0);
    const composed = parts.join(' ');
    return (
      <div className="space-y-2">
        <FieldHeader label={label} subLabel={subLabel} required={required} />
        <p className="text-sm">
          {composed ? (
            composed
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader label={label} subLabel={subLabel} required={required} />
      <div className="flex gap-3">
        {showPrefix && (
          <div className="space-y-1 w-20">
            <Input
              id={`${element.id}-prefix`}
              type="text"
              placeholder="Mr."
              value={value?.prefix || ''}
              onChange={(e) => handleChange('prefix', e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">Prefix</p>
          </div>
        )}
        <div className="space-y-1 flex-1">
          <Input
            id={`${element.id}-first`}
            type="text"
            placeholder="First name"
            value={value?.firstName || ''}
            onChange={(e) => handleChange('firstName', e.target.value)}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">First</p>
        </div>
        {showMiddleName && (
          <div className="space-y-1 flex-1">
            <Input
              id={`${element.id}-middle`}
              type="text"
              placeholder="Middle name"
              value={value?.middleName || ''}
              onChange={(e) => handleChange('middleName', e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">Middle</p>
          </div>
        )}
        <div className="space-y-1 flex-1">
          <Input
            id={`${element.id}-last`}
            type="text"
            placeholder="Last name"
            value={value?.lastName || ''}
            onChange={(e) => handleChange('lastName', e.target.value)}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">Last</p>
        </div>
        {showSuffix && (
          <div className="space-y-1 w-20">
            <Input
              id={`${element.id}-suffix`}
              type="text"
              placeholder="Jr."
              value={value?.suffix || ''}
              onChange={(e) => handleChange('suffix', e.target.value)}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">Suffix</p>
          </div>
        )}
      </div>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default NameInput;
